import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { SortPropDir } from '@swimlane/ngx-datatable';
import { take } from 'rxjs/operators';
import { errorless } from '@app/core/helpers/errorless';
import { TableDefinition } from '@app/shared/dashboard-table/table-definitions/table-definition';
import { CommunicationsFacade } from '@app/communications/state/communications.facade';
import { DashboardTableAdvancedSearchComponent } from '@app/shared/dashboard-table-advanced-search/dashboard-table-advanced-search.component';
import { GenericFilterField } from '@app/shared/filter/inputs/filter-field';
import { TextFilterField } from '@app/shared/filter/inputs/text.filter-field';
import { CustomClaimSmsResponse } from '@app/communications/models/responses/custom-claim-sms-response';
import { AuthenticationService } from '@app/authentication/services/authentication.service';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { CommunicationPermissions } from '@app/communications/models/enums/communication-permissions';

const filterFields: GenericFilterField[] = [
  new TextFilterField('recipient', 'Recipient'),
  new TextFilterField('claimCode', 'Claim No.'),
  new TextFilterField('region', 'Region'),
];

@Injectable()
export class CustomClaimSmsRequestTableDefinition extends TableDefinition<CustomClaimSmsResponse> {
  constructor(
    private facade: CommunicationsFacade,
    private modalService: NgbModal,
    private authService: AuthenticationService,
    private router: Router,
  ) {
    super();
    this.title = 'SMS Authorisations';

    this.showSearch = true;
    this.showFilter = true;

    this.columns = [
      { name: 'Claim Code', prop: 'claimCode', width: 200 },
      { name: 'Recipient', prop: 'recipient', width: 200 },
      { name: 'Region', prop: 'region', width: 200 },
      { name: 'Date Requested', prop: 'requestedDate', width: 200 },
      { name: 'Requested By', prop: 'requestedBy', width: 200 },
      { name: 'Message', prop: 'message', width: 750 },
    ];

    this.rows$ = this.facade.customClaimSmsRequests$;
  }

  loadInitialData(): void {
    this.facade.resetCustomClaimSmsRequests(true);
  }

  sort(sort: SortPropDir): void {
    this.facade.sortCustomClaimSmsRequests(sort);
  }

  select(request: CustomClaimSmsResponse): void {
    this.router.navigate([
      'claims-administration',
      'claim-overview',
      request.claimCode,
      'contact',
      request.claimContactId,
      'custom-sms',
      request.requestId,
    ]);
  }

  loadMore({ offset, limit }: { offset: number; limit: number }): void {
    this.facade.loadMoreCustomClaimSmsRequests(limit, offset);
  }

  registerSubscriptions(): void {
    this.subscriptions.push(
      this.facade.customClaimSmsRequestsIsLoading$.subscribe((loading) => {
        this.loadData(loading);
      }),
    );
  }

  simpleSearch(searchString: string): void {
    this.facade.searchCustomClaimSmsRequests(searchString);
  }

  advancedSearch(): void {
    this.modalService.dismissAll();
    const modalTitle = 'Award Pension Search';
    this.rows$.pipe(take(1)).subscribe(async ({ searchData }) => {
      const pensionModal = this.modalService.open(
        DashboardTableAdvancedSearchComponent,
        {
          size: 'lg',
        },
      );
      pensionModal.componentInstance.searchData = searchData;
      pensionModal.componentInstance.title = modalTitle;
      pensionModal.componentInstance.fields = filterFields;
      const formData = await errorless(pensionModal.result);
      this.facade.searchCustomClaimSmsRequests(formData);
    });
  }

  get isVisible(): boolean {
    return this.authService.hasPermission(
      CommunicationPermissions.GetClaimsCustomSMSRequests,
    );
  }
}
